import { useEffect, useState } from "react";

import {
  handleSetUpdatedAccountGroup,
  handleGetUpdatedAccountGroup,
  handleRemoveUpdatedAccountGroup,
} from "../Packs/packs.utils";
import { useAppStore } from "@/global/store";
import { pluralize } from "@/global/utils/utils";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useDynamicPricingModal } from "../modals/DynamicPricingModal";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";

import type { DynamicPricingType } from "@/types";

export function useDynamicPricing() {
  const dynamicPricingModal = useDynamicPricingModal();
  const {
    packs,
    buckets,
    teamSchema,
    dynamicPricing,
    dynamicPricingForPackages,
  } = useTeamConfig();
  const [selectedPricing, setSelectedPricing] = useState<DynamicPricingType | null>(null); // prettier-ignore

  /** states */
  const selectedEvents = useAppStore(selectEventsSelected);
  const isDynamicPricingEnabled = dynamicPricing?.enabled || false;
  const isPacksEnabled = packs?.enabled || false;
  const isBucketsEnabled = (!!buckets && buckets.enabled) || false;
  const dynamicPricingForPackagesEnabled = dynamicPricingForPackages?.enabled || false; // prettier-ignore

  /** Function to generate dynamic discount message */
  // prettier-ignore
  function generateMessage(gamesToAdd: number, discount: number, customAditionalMessage?: string): string {
    return `Would you like to add ${gamesToAdd} more ${gamesToAdd > 1 ? "games" : "game"} ${customAditionalMessage ? customAditionalMessage : ` and get ${discount}% off your tickets?`}`;
  }

  function generateOfferMoreMessage(dynamicPrices: DynamicPricingType[]) {
    const showAllDiscounts = dynamicPricing?.showAllDiscounts || false;
    const selectedEventsQuantity = selectedEvents.length;

    // Filter out items with hideOfferFromModal set to true
    const filteredPrices = dynamicPrices.filter((dp) => !dp.hideOfferFromModal);

    // If showAllDiscounts is false, show only the first discount option
    const applicablePrices = showAllDiscounts
      ? filteredPrices
      : filteredPrices.slice(0, 1);

    if (applicablePrices.length === 0) return "No offers available.";
    let isCustomMsg = false;
    let isCustomDiscountMsg = false;

    if (applicablePrices.length === 1 && applicablePrices[0].offerMoreMessage)
      return applicablePrices[0].offerMoreMessage;

    const messageParts = applicablePrices.map((dp, index) => {
      const gamesToAdd = dp.minSelectedAmount - selectedEventsQuantity;
      const customAditionalMessage = dp?.customAditionalMessage;
      const customDiscountMessage = dp?.customDiscountMessage;
      isCustomMsg = customAditionalMessage ? true : false;
      isCustomDiscountMsg = customDiscountMessage ? true : false;
      const discount = dp.discount;
      return `add ${gamesToAdd} more ${pluralize("game", gamesToAdd)} ${
        customAditionalMessage
          ? customAditionalMessage
          : customDiscountMessage
          ? customDiscountMessage
          : `and get ${discount}% off`
      }`;
    });

    // Join all parts with "or" for dynamic message creation
    const finalMessage = `Would you like to ${messageParts.join(" or ")}${
      isCustomMsg ? "" : isCustomDiscountMsg ? "?" : ` your tickets?`
    }`;

    return finalMessage;
  }

  // prettier-ignore
  function getApplicableDynamicPricing(selectedEventsQuantity: number,dynamicPricingList: DynamicPricingType[]) {
    const sortedPricingList = dynamicPricingList.sort((a, b) => a.minSelectedAmount - b.minSelectedAmount);
    const previousTier = sortedPricingList.slice().reverse().find((tier) => selectedEventsQuantity >= tier.minSelectedAmount);
    return previousTier || null;
  }

  /** Function to set updated account group */
  // prettier-ignore
  function onUpdateAccountGroup(dynamicPricing: DynamicPricingType) {
    setSelectedPricing(dynamicPricing);
    handleSetUpdatedAccountGroup([String(dynamicPricing.updatedAccountGroup)], teamSchema);
  }

  /** Function to handle dynamic pricing based on selected events amount */
  // prettier-ignore
  function handleDynamicPrices() {
    if (!isDynamicPricingEnabled) return;

    const selectedEventsQuantity = selectedEvents.length;

    const updatedAccountGroup = handleGetUpdatedAccountGroup(teamSchema);
    const dynamicPricingMatchingAccountGroup = dynamicPricing?.list.find((item) => updatedAccountGroup?.includes(String(item.updatedAccountGroup)));
    if (dynamicPricingMatchingAccountGroup && dynamicPricingMatchingAccountGroup.minSelectedAmount > selectedEventsQuantity) {
      const previousDynamicPricing = dynamicPricing?.list.find((item) => item.id == dynamicPricingMatchingAccountGroup.id - 1);
      if (previousDynamicPricing) onUpdateAccountGroup(previousDynamicPricing);
    }

    const nextDynamicPrices = dynamicPricing?.list.filter((item) => item.minSelectedAmount > selectedEventsQuantity);

    if (nextDynamicPrices?.length && selectedPricing && dynamicPricingMatchingAccountGroup)
      setSelectedPricing({
        ...selectedPricing,
        offerMoreMessage: generateOfferMoreMessage(nextDynamicPrices),
        customProgressBarMessage: dynamicPricingMatchingAccountGroup.customProgressBarMessage,
      });

    const accountGroupMatchingEventsQuantity = dynamicPricing?.list.find((item) => selectedEventsQuantity == item.minSelectedAmount);
    if (accountGroupMatchingEventsQuantity) {
      const nextDynamicPrices = dynamicPricing?.list.filter((item) => item.id >= accountGroupMatchingEventsQuantity.id + 1);
      if (nextDynamicPrices?.length)
        accountGroupMatchingEventsQuantity.offerMoreMessage = generateOfferMoreMessage(nextDynamicPrices);

      onUpdateAccountGroup(accountGroupMatchingEventsQuantity);
    }

    const lastDynamicPricing = dynamicPricing?.list[dynamicPricing.list.length - 1];
    if (lastDynamicPricing && selectedEventsQuantity >= lastDynamicPricing.minSelectedAmount)
      onUpdateAccountGroup(lastDynamicPricing);

    if (dynamicPricing?.list) {
      const inBetweenDynamicPricing = getApplicableDynamicPricing(selectedEventsQuantity, dynamicPricing.list);
      if (inBetweenDynamicPricing) onUpdateAccountGroup(inBetweenDynamicPricing);
    }
  }

  function handleShouldOpenDynamicPricingModal(): boolean {
    if (
      !isDynamicPricingEnabled ||
      !dynamicPricing ||
      dynamicPricing.dontOpenModal
    )
      return false;

    const shouldOpenDynamicPricingModal =
      isDynamicPricingEnabled &&
      !dynamicPricingModal.hasModalOpened &&
      !!selectedPricing &&
      dynamicPricing.list.some(
        (itm) => itm.minSelectedAmount > selectedEvents.length
      );

    if (shouldOpenDynamicPricingModal) return true;
    return false;
  }

  useEffect(() => {
    // prettier-ignore
    if (isDynamicPricingEnabled) handleDynamicPrices();
  }, [selectedEvents, isDynamicPricingEnabled]);

  // handling teams that removed dynamic pricing
  useEffect(() => {
    // if dynamic pricing is not enabled, need to check if there is a selected pricing on local storage and remove it
    // prettier-ignore
    if (teamSchema && !isDynamicPricingEnabled && !isPacksEnabled && !isBucketsEnabled && !dynamicPricingForPackagesEnabled) {
      const updatedAccountGroup = handleGetUpdatedAccountGroup(teamSchema);
      if (updatedAccountGroup) handleRemoveUpdatedAccountGroup(teamSchema);
    }
  }, []);

  return {
    selectedPricing,
    dynamicPricingModal,
    isDynamicPricingEnabled,
    handleShouldOpenDynamicPricingModal,
  };
}
